<template>
  <div class="flex flex-col pt-16 md:py-16">
    <RevInputFileUpload
      :id
      v-model="attachments"
      :accept="ACCEPTED_FILE_EXTENSIONS"
      :description="i18n(translations.inputDescription)"
      file-preview-display="grid"
      :label-empty="i18n(translations.inputLabel)"
      :label-not-empty="i18n(translations.inputLabel)"
      :max-file-size="MAX_FILE_SIZE"
      :max-files="MAX_FILES"
      @click="() => $emit('click')"
      @errors="handleErrors"
    />

    <!-- NB:  this condition avoids screen flickering  
     The files fetch and update of files/pending is done in an asynchronous function
     It seems that because of this Vue doesn't update VDOM / DOM updates.
     To ensure that we hide the skeleton and show the files during the same update cycle, we need to add isEmpty(attachments) 
    -->
    <div
      v-if="pending && isEmpty(attachments)"
      class="mt-8 grid grid-cols-1 gap-16 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2"
    >
      <RevSkeleton
        v-for="_n in initialAttachments.length"
        :key="_n"
        height="206px"
        shape="rectangle"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'
import { RevSkeleton } from '@ds/components/Skeleton'

import type { UploadedFileValue } from '~/api/reviews/types/GetReviewCollectionResponse'

import { convertUploadedValuesToFiles } from '../../helpers/buildFiles'

import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILES,
  MAX_FILE_SIZE,
} from './QuestionFileUpload.constants'
import translations from './QuestionFileUpload.translations'

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast } = useTheToast()

const props = defineProps<{
  id: string
  title: string
  initialAttachments: UploadedFileValue[]
}>()

const attachments = defineModel<File[]>({
  default: () => [],
})

const hasFilesToConvert =
  props.initialAttachments &&
  props.initialAttachments.length > 0 &&
  attachments?.value?.length === 0

const pending = ref(hasFilesToConvert)

defineEmits<{
  (e: 'click'): void
}>()

onMounted(async () => {
  if (hasFilesToConvert) {
    try {
      pending.value = true
      const files = await convertUploadedValuesToFiles(props.initialAttachments)

      attachments.value = files
    } catch (error) {
      logger.error('[ReviewForm] Load uploaded files', {
        error: error as Error,
        owners: ['bot-squad-content-front'],
      })
    } finally {
      pending.value = false
    }
  }
})

function handleErrors(errors: FileError[]) {
  // Use new Set to remove duplicates of the same error message
  const fileErrorMessage = [
    ...new Set(
      errors.map((error) => {
        switch (error.type) {
          case 'MAX_FILES_EXCEEDED':
            return i18n(translations.errorTooManyFiles)
          case 'MAX_FILE_SIZE_EXCEEDED':
            return i18n(translations.errorTooBig)
          case 'WRONG_FILE_EXTENSION':
            return i18n(translations.errorWrongFormat)
          default:
            return i18n(translations.errorGeneric)
        }
      }),
    ),
  ].join(' ')

  openErrorToast({
    content: fileErrorMessage,
  })
}
</script>
